import { Constants } from "@/constants";
import { ActionResult } from "@/store/modules/tasks/tasks";
import storeHelper from "@/store/store-helper";
import { Controllers, IsBadRequestResult, IsServerErrorResult, ProblemResult } from "@infoland/iprova-api";

interface TaskBase {
    id: string;
    assigned_to_team: TeamAssignment;
    title: string;
    description: string;
    assigned_to_user: UserAssignment;
    read: boolean;
    divider_text?: string;
    action_links: TaskActionLink[];
    is_important: boolean;
    group: TaskGroup;
    runtimetask_type: number;
    subscription_type: number;
    subscription_id: number;
}

export interface TeamAssignment {
    team_id: string;
    name: string;
}

export interface UserAssignment {
    user_id: string;
    user_name: string;
}

export interface TaskGroup {
    group_id: number;
    fixed_name: string;
    display_name: string;
    count?: number;
    selected?: boolean;
}

export interface TaskActionLink {
    title: string;
    url: string;
    order: number;
    target_window: string;
}

export interface ApiTask extends TaskBase {
    notification_id?: string;
    task_id?: string;
    deadline_date?: string;
    created_date_time: string;
    postponed_until_date?: string;
    last_modified_date_time: string;
    finished_date_time?: string;
    finished: boolean;
    notifications: ApiTask[];
}

export interface Task extends TaskBase {
    deadline_date_time?: Date;
    created_date_time: Date;
    postponed_until_date?: Date;
    last_modified_date_time: Date;
    finished_date_time?: Date;
    finished: boolean;
    notifications: Task[];
}

export interface ApiPostTask {
    title: string;
    description?: string;
    deadline_date?: string;
    is_important: boolean;
    assigned_to_team_id: string;
    assigned_to_user_id?: string;
}

export interface ApiPatchTask {
    title?: string;
    description?: string;
    deadline_date?: string;
    finished?: boolean;
    assigned_to_user_id?: string;
}

export function mapTasks(tasks: ApiTask[]): Task[] {
    return tasks.map(
        t =>
            <Task>{
                id: t.task_id || t.notification_id,
                title: t.title,
                description: t.description,
                deadline_date_time: t.deadline_date ? t.deadline_date.toDate() : null,
                created_date_time: t.created_date_time.toDate(),
                read: t.read,
                postponed_until_date: t.postponed_until_date ? t.postponed_until_date.toDate() : null,
                divider_text: t.divider_text,
                action_links: t.action_links,
                is_important: t.is_important,
                last_modified_date_time: t.last_modified_date_time.toDate(),
                finished_date_time: t.finished_date_time ? t.finished_date_time.toDate() : null,
                group: t.group,
                finished: t.finished,
                assigned_to_team: t.assigned_to_team,
                assigned_to_user: t.assigned_to_user,
                notifications: t.notifications ? mapTasks(t.notifications) : [],
                runtimetask_type: t.runtimetask_type,
                subscription_id: t.subscription_id,
                subscription_type: t.subscription_type
            }
    );
}

export async function loadMyTasksAsync(): Promise<Task[]> {
    const myTaskData: ApiTask[] = await Controllers.GenericController.getAsync<ApiTask[]>(
        `users/me/tasks?limit=${Constants.Task.maxTaskLimit}&finished=false&include_notifications=true&assignment_types=unassigned,assigned_to_me`
    );
    return mapTasks(myTaskData);
}

export async function loadMyNotificationsAsync(): Promise<Task[]> {
    const myNotificationData: ApiTask[] = await Controllers.GenericController.getAsync<ApiTask[]>(
        `users/me/notifications?limit=${Constants.Task.maxNotificationLimit}&finished=false&notification_type=general`
    );
    return mapTasks(myNotificationData);
}

export async function loadTasksForTeamAsync(teamId: string): Promise<Task[]> {
    const teamTaskData: ApiTask[] = await Controllers.GenericController.getAsync<ApiTask[]>(
        `teams/${teamId}/tasks?limit=${Constants.Task.maxTaskLimit}&finished=false&include_notifications=true`
    );
    return mapTasks(teamTaskData);
}

// Create an Adhoc team task
export async function createTeamTaskAsync(task: ApiPostTask): Promise<ActionResult<string, string>> {
    const result = await Controllers.GenericController.postAsync<ApiPostTask, string | ProblemResult<string>>("tasks", task);
    const created_identifier: string = (result as any)?.created_identifier;
    if (created_identifier) {
        return {
            success: true,
            successData: created_identifier
        };
    } else if (IsBadRequestResult(result)) {
        return {
            success: false,
            errorData: result.title
        };
    } else return { success: false };
}

export async function updateTeamTaskAsync(taskID: string, task: ApiPatchTask): Promise<ActionResult<void, string>> {
    const result = await Controllers.GenericController.patchAsync<ApiPatchTask, void | ProblemResult<string>>(`tasks/${taskID}`, task);
    const returnValue = handleTaskMutationResult(result);

    return returnValue;
}

export async function deleteTeamTaskAsync(task_id: string): Promise<ActionResult<void, string>> {
    const result = await Controllers.GenericController.deleteAsync<null, void | ProblemResult<string>>(`/tasks/${task_id}`);

    return handleTaskMutationResult(result);
}

function handleTaskMutationResult(result: void | ProblemResult<string>): ActionResult<void, string> {
    if (IsServerErrorResult(result)) {
        storeHelper.siteStore.setSnackBarServerErrorMessage(result);
        return { success: false };
    } else if (IsBadRequestResult(result)) {
        if (result.extrainfo) {
            storeHelper.siteStore.setSnackBarMessageLookup("teams.errorMessages." + result.extrainfo, true);
        } else {
            storeHelper.siteStore.setSnackBarMessageLookup("general.messages.error", true);
        }

        return { success: false, errorData: result.extrainfo };
    } else return { success: true };
}

export async function loadNotificationsForTeamAsync(teamId: string): Promise<Task[]> {
    const teamNotificationData = await Controllers.GenericController.getAsync<ApiTask[]>(`teams/${teamId}/notifications?limit=1000`);
    return mapTasks(teamNotificationData);
}
